/**
 * Selection for the listing pickers, stored as an intent plus the exceptions to it
 * rather than as the selected set.
 *
 * "Every listing starts checked" over a portfolio of tens of thousands of units can't be
 * a set of ids — building it means holding the whole portfolio, and connecting with it
 * means POSTing the whole portfolio back. A partner who unchecks three communities sends
 * three ids either way.
 */
export type SelectionMode = "all" | "none";

export interface ListingSelection {
  mode: SelectionMode;
  /** Units that go against `mode`: unchecked when "all", checked when "none". */
  exceptions: Set<number>;
}

export const selectAll = (): ListingSelection => ({
  mode: "all",
  exceptions: new Set()
});

export const selectNone = (): ListingSelection => ({
  mode: "none",
  exceptions: new Set()
});

export const isUnitSelected = (
  selection: ListingSelection,
  unitId: number
): boolean =>
  selection.mode === "all"
    ? !selection.exceptions.has(unitId)
    : selection.exceptions.has(unitId);

export const setUnitsSelected = (
  selection: ListingSelection,
  unitIds: number[],
  selected: boolean
): ListingSelection => {
  const exceptions = new Set(selection.exceptions);
  const isException = selection.mode === "all" ? !selected : selected;

  unitIds.forEach((id) =>
    isException ? exceptions.add(id) : exceptions.delete(id)
  );

  return { mode: selection.mode, exceptions };
};

/** `total` is the number of eligible listings; exceptions only ever hold eligible ids. */
export const selectedCount = (
  selection: ListingSelection,
  total: number
): number =>
  selection.mode === "all"
    ? Math.max(total - selection.exceptions.size, 0)
    : selection.exceptions.size;

/**
 * "All but these" and "only these" are both expressed as a short id list, so the request
 * body stays proportional to what the partner changed, not to the portfolio.
 */
export const connectPayload = (
  selection: ListingSelection
): { unit_ids: number[] } | { excluded_unit_ids: number[] } =>
  selection.mode === "all"
    ? { excluded_unit_ids: [...selection.exceptions] }
    : { unit_ids: [...selection.exceptions] };
